'use client';

/**
 * @file SelectionList.tsx
 * ─────────────────────────────────────────────────────────────
 * 라디오 / 체크박스 그룹 리스트 컴포넌트.
 *
 * ── type ───────────────────────────────────────────────────
 *   'radio'     — 단일 선택
 *   'checkbox'  — 다중 선택
 *
 * ── required ───────────────────────────────────────────────
 *   required                    → '상품을 1개 이상 선택해 주세요.'
 *   required requiredLabel="옵션" → '옵션을 1개 이상 선택해 주세요.'
 * ─────────────────────────────────────────────────────────────
 */

import clsx from 'clsx';
import { createContext, useId, useMemo, type ReactNode } from 'react';
import styles from './SelectionList.module.scss';
import FormMessage from '@publishing/components/forms/_FormMessage';
export type SelectionListType = 'radio' | 'checkbox';

export interface SelectionListProps {
  type: SelectionListType;
  children: ReactNode;
  className?: string;
  /** `radio` / `checkbox` 그룹의 `input` `name`. 생략 시 내부에서 `useId`로 생성 */
  name?: string;
  /** 필수 선택 여부 */
  required?: boolean;
  /** 에러 메시지 앞 명사. 기본값 '상품' */
  requiredLabel?: string;
  /** 선택된 값 배열 — required와 함께 쓰면 내부에서 선택 여부 판단 */
  value?: string[];
}

export interface SelectionListContextValue {
  type: SelectionListType;
  name: string;
}

export const SelectionListContext = createContext<SelectionListContextValue | null>(null);

export default function SelectionList({ 
  type,
  children,
  className,
  name: nameProp,
  required = false,
  requiredLabel = '상품',
  value,
}: SelectionListProps) {
  const reactId = useId();
  const name = nameProp ?? `selection-list-${reactId.replace(/:/g, '')}`;
  const contextValue = useMemo(() => ({ type, name }), [type, name]);

  const resolvedError =
    required && value !== undefined && value.length === 0
      ? `${requiredLabel}을 1개 이상 선택해 주세요.`
      : undefined;

  return (
    <SelectionListContext.Provider value={contextValue} >
      <ul className={clsx(styles.list, className)}>
        {children}
      </ul>
      <FormMessage errorMessage={resolvedError} />
    </SelectionListContext.Provider>
  );
}
